import {useState} from 'react';
import {useForm} from 'react-hook-form';
import {useMutation, useQueryClient} from 'react-query';
import cx from 'classnames';
import {toast} from 'react-toastify';
import _ from 'lodash';

import {trackEvent} from '../../utils/tracking';
import {ToastMessage} from '../../core/components/toast';
import {createDataset, NormalizedDataset} from '../../models/dataset';
import {CSVUpload} from '../file-upload/csv-upload';
import {DatasetUpdateRequest, updateDatasetReports} from '../../models/dataset';
import {AxiosError} from 'axios';
import {useAxios} from 'src/utils/http';
import {useAuth} from 'src/hooks/auth';
import {useProjectContext} from 'src/models/project-context';

interface CreateDatasetFields {
  name: string;
}

export const CreateDatasetForm = ({
  closeModal = () => {},
  setLoading = () => {},
  selectedStudyIDs = [],
  allowCSVUpload = false,
  created,
  projectID,
}: {
  closeModal?: () => void;
  setLoading?: (loading: boolean) => void;
  allowCSVUpload?: boolean;
  selectedStudyIDs?: string[];
  projectID?: string;
  created?: (dataset: NormalizedDataset) => void;
}) => {
  const [uploadedCSV, uploadedCSVChange] = useState(false);
  const [csvStudyIDs, csvStudyIDsChange] = useState<string[]>([]);
  const {register, handleSubmit, errors} = useForm<CreateDatasetFields>();

  const queryClient = useQueryClient();
  const http = useAxios();
  const {authState} = useAuth();

  const {projectId: projectIdFromContext} = useProjectContext();
  if (!projectID && !!projectIdFromContext) {
    projectID = projectIdFromContext;
  }

  const createDatasetMut = useMutation(
    (form: {name: string; projectID?: string}) => createDataset(http, form),
    {
      onMutate: () => {
        setLoading(true);
        closeModal();
      },
      onSuccess: dataset => {
        const updateRequest: DatasetUpdateRequest = {
          add: {
            query: undefined,
            ids: [...csvStudyIDs, ...selectedStudyIDs],
          },
          remove: {
            query: undefined,
            ids: [],
          },
        };

        _.defer(() => {
          trackEvent('CLICK_CREATE_DATASET', {project_id: projectID});
          created && created(dataset);
          queryClient.invalidateQueries([
            'projectsByOwner',
            authState.profile?.userId,
          ]);
          queryClient.invalidateQueries([
            'datasetsByOwner',
            authState.profile?.userId,
          ]);
          updateDatasetReports(
            http,
            dataset.id,
            updateRequest,
            'add',
            true
          ).then(({addedStudiesCount}) => {
            queryClient.invalidateQueries(['dataset'], {exact: true});
            queryClient.invalidateQueries(['dataset', dataset.id]);
            queryClient.removeQueries(['dataset']);
            setLoading(false);
            toast(<ToastMessage title="Dataset created" icon="success" />);
            if (uploadedCSV) {
              trackEvent('CREATE_DATASET_FROM_CSV', {datasetID: dataset.id});
              if (addedStudiesCount !== csvStudyIDs.length) {
                toast(
                  <ToastMessage title={'WARNING'} icon="warning">
                    Some studies could not be added to the dataset because they
                    no longer exist.
                  </ToastMessage>
                );
              }
            }
          });
        });
      },
      onError: (err: AxiosError) => {
        const message =
          (err?.response?.data as any)?.message ?? 'Error creating dataset';
        toast(<ToastMessage title={message} icon="error" />);
        setLoading(false);
      },
    }
  );

  const onSubmit = handleSubmit((form: CreateDatasetFields) => {
    createDatasetMut.mutate({
      name: form.name,
      projectID: projectID,
    });
  });

  return (
    <form
      onSubmit={e => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <div className="text-lg mb-3">New Dataset</div>
      <div className="mb-3">
        <label htmlFor="name" className="input-label">
          Name
        </label>
        <input
          name="name"
          type="text"
          data-cy="CreateDatasetForm_nameInput"
          className={cx('text-input w-full mt-2', {
            'text-input-error': !!errors.name,
          })}
          autoComplete="off"
          ref={register({required: true})}
        />
      </div>
      <div className="flex flex-row-reverse justify-between">
        <button
          className="btn btn-primary max-h-8 ml-2 min-w-max"
          type="submit"
          disabled={createDatasetMut.isLoading}
        >
          Create Dataset
        </button>

        {allowCSVUpload &&
          (uploadedCSV ? (
            <CSVUpload
              onCSVUpload={studyIDs => {
                csvStudyIDsChange(studyIDs);
              }}
            />
          ) : (
            <div
              className="text-blue-700 text-xs underline hover:cursor-pointer"
              onClick={() => {
                uploadedCSVChange(true);
              }}
            >
              Or upload a .csv file
            </div>
          ))}
      </div>
    </form>
  );
};
